import { InputAdornment, Stack, TextField } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { forwardRef, Fragment, useState, type AnchorHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { NavItem, NavItemButton } from '@omnea-app/fe-app-shell/side-nav/NavItem'
import { NavDivider, NavSection } from '@omnea-app/fe-app-shell/side-nav/NavSection'
import { SideNav } from '@omnea-app/fe-app-shell/side-nav/SideNav'
import { hrefOfMenuItem, isMenuItemSelected, recordHrefsOf, visibleSectionsOf } from '@omnea-app/fe-app-shell/sidebar/sectionItem'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { ArrowLeftOMIcon, SearchOMIcon } from '@omnea-app/icons'
import { useOriginalPath } from '../sidebar/hooks/useOriginalPath'
import { useSettingsSections } from '../sidebar/settings/useSettingsSections'

// Links that leave the app, such as the public API docs, open in a new tab.
const ExternalLink = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>(function ExternalLink(props, ref) {
  return <a ref={ref} {...props} target="_blank" rel="noopener noreferrer" />
})

/** The app shell's nav on settings pages: a way back to the app, a search, and the settings areas the user can reach. */
export const ShellSettingsNav = () => {
  const { t } = useTranslation('common')
  const { t: tSettings } = useTranslation('settings')
  const { asPath } = useRouter()
  const routeAccess = useRouteAccess()
  const backHref = useOriginalPath(true)
  const [query, setQuery] = useState('')

  const sections = useSettingsSections()
  const recordHrefs = recordHrefsOf(sections)
  const visibleSections = visibleSectionsOf(sections, routeAccess, query)

  return (
    <SideNav
      aria-label={t('sidebar.settings')}
      wide
      header={
        <>
          <Stack sx={{ height: '3.5rem', justifyContent: 'center' }}>
            <NavItemButton icon={<ArrowLeftOMIcon />} label={t('sidebar.back-to-app')} href={backHref} LinkComponent={Link} />
          </Stack>
          <TextField
            placeholder={tSettings('search-placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': tSettings('search-label') },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOMIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </>
      }
    >
      {visibleSections.map((section, index) => (
        <Fragment key={section.title}>
          {index > 0 && <NavDivider />}
          <NavSection title={section.title}>
            {section.items.map((item) => (
              <NavItem
                key={hrefOfMenuItem(item)}
                icon={item.icon}
                label={item.label}
                badge={item.showsOmneanOnlyBadge === true ? t('sidebar.omnean-only') : undefined}
                selected={isMenuItemSelected(item, asPath, recordHrefs)}
                href={hrefOfMenuItem(item)}
                LinkComponent={item.destination.kind === 'external' ? ExternalLink : Link}
              />
            ))}
          </NavSection>
        </Fragment>
      ))}
    </SideNav>
  )
}
